import axios from "axios";
import {
  DetailedReportReponse,
  FeedbackData,
  JDData,
  JobRoleData,
  LoginResponse,
  ReportRequest,
  SubmitFeedbackRequest,
  SubmitRITMRequest,
  SummaryReportResponse,
  UpdateJDRequest,
  UpdateSquadRequest,
  VendorData,
  VendorListResponse,
} from "../commonTypes";
import { API_URL } from "../constants";
import { encrypt, getSessionStorage } from "../utils";

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
 
 
 
    // Server / Gateway down
    if (error.response && error.response.status >= 500 || error.code === "ERR_CONNECTION_REFUSED" || error.message.includes("Network Error")) {
      // Dispatch event - PrivateRoute will handle alert + redirect with delay
      window.dispatchEvent(new Event("server-down-error"));
      return Promise.reject(error);
    }
 
    return Promise.reject(error);
  }
);
 

axios.interceptors.request.use(
  (config) => {
    if (!navigator.onLine) {
      return Promise.reject({ isOffline: true });
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const authentication = async (
  username: string,
  password: string
): Promise<LoginResponse | void> => {
  const headers = {
    "Content-Type": "application/json",
  };

  return await axios
    .post<LoginResponse>(
      API_URL.AUTH,
      {
        username,
        password,
      },
      { headers, withCredentials: true }
    )
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      if (error.response && error.response.status === 401) {
        throw Error(error.response);
      }
    });
};

export const logoutUser = async (): Promise<void> => {
  const config = {
    withCredentials: true,
  };

  try {
    await axios.post(API_URL.LOGOUT, {}, config);
  } catch (error: any) {
    // Handle logout errors silently or log them
    console.error("Logout API error:", error);
  }
};

export const getJobDescriptionsList = async (): Promise<JDData[]> => {
  return await getAPI(API_URL.JD_LIST);
};

export const getRolesList = async (): Promise<JobRoleData[]> => {
  return await getAPI(API_URL.JOB_ROLE_LIST);
};
export const getAuditTypes = async (): Promise<any[]> => {
  return await getAPI(API_URL.AUDIT_TYPES_LIST);
};
export const getTeamMembers = async (): Promise<any[]> => {
  return await getAPI(API_URL.TEAM_MEMBERS_LIST);
};

export const getAuditees = async (): Promise<any[]> => {
  return await getAPI(API_URL.AUDITEES_LIST);
};
export const getTeamlead = async (): Promise<any[]> => {
  return await getAPI(API_URL.TEAMLEAD_LIST);
};

export const getAuditModes = async (): Promise<any[]> => {
  return await getAPI(API_URL.AUDIT_MODES_LIST);
};
export const getTeams = async (): Promise<any[]> => {
  return await getAPI(API_URL.TEAMS_LIST);
};

export const getFeedbackList = async (): Promise<FeedbackData[]> => {
  return await getAPI(API_URL.FEEDBACKS_LIST);
};

export const getJD = async (id: string): Promise<JDData> => {
  return getAPI(API_URL.JD_VIEW + "/" + id);
};

export const getVendor = async (id: string): Promise<VendorData> => {
  return getAPI(API_URL.VENDOR_VIEW + "/" + id);
};

export const getVendorList = async (): Promise<VendorData[]> => {
  return getAPI(API_URL.VENDORS_LIST);
};

export const getJDByRole = async (id: string): Promise<JDData> => {
  return getAPI(API_URL.JD_BY_ROLE + "/" + id);
};
export const submitJD = async (formData: any): Promise<string> => {
  return postAPI(API_URL.JD_CREATE, formData);
};
export const getPerformanceSummaryReport = async (
  formData: ReportRequest
): Promise<SummaryReportResponse> => {
  return postAPI(API_URL.PERFORMANCE_REPORT_SUMMARY, formData);
};

export const getPerformanceDetailedReport = async (
  formData: ReportRequest
): Promise<DetailedReportReponse[]> => {
  return postAPI(API_URL.PERFORMANCE_REPORT_DETAILED, formData);
};

export const submitFeedback = async (
  formData: SubmitFeedbackRequest
): Promise<string> => {
  return postAPI(API_URL.FEEDBACK_CREATE, formData);
};

export const submitRITM = async (
  formData: SubmitRITMRequest
): Promise<string> => {
  return postAPI(API_URL.RITM_UPDATE, formData);
};

export const updateJD = async (formData: UpdateJDRequest): Promise<string> => {
  return postAPI(API_URL.JD_UPDATE, formData);
};

export const updateVendor = async (formData: any): Promise<any> => {
  return postAPI(API_URL.VENDOR_UPDATE, formData);
};

export const deleteJobDescription = async (id: string): Promise<any> => {
  return deleteAPI(API_URL.JD_DELETE + "/" + id);
};

export const deleteVendor = async (id: string): Promise<any> => {
  return deleteAPI(API_URL.VENDOR_DELETE + "/" + id);
};

export const deleteUser = async (id: string): Promise<any> => {
  return deleteAPI(API_URL.USER_DELETE + "/" + id);
};

export const submitRoleRequest = async (formData: any): Promise<any> => {
  return postAPI(API_URL.CREATE_ROLE_REQUEST, formData);
};

export const kickoffMeeting = async (formData: any): Promise<any> => {
  return postAPI(API_URL.KICKOFF_MEETING, formData);
};

export const createAuditPlanning = async (formData: any): Promise<any> => {
  return postAPI(API_URL.CREATE_AUDIT_PLANNING, formData);
};
export const createNextSequenceQuestion = async (formData: any): Promise<any> => {
  return postAPI(API_URL.SEND_NEXT_SEQUENCE_QUESTION, formData);
};
export const generateNextSequenceQuestion = async (formData: any): Promise<any> => {
  return postAPI(API_URL.GENERATE_NEXT_SEQUENCE_QUESTION, formData);
};
export const createFollowupQuestion = async (formData: any): Promise<any> => {
  return postAPI(API_URL.FOLLOWUP_QUESTION, formData);
};
export const generateManualReview = async (auditid: any, organizationid: any, sequenceorder: any): Promise<any> => {
  return getAPI(`${API_URL.MANUAL_REVIEW}?a=${encrypt(auditid)}&t=${encrypt(organizationid)}&s=${encrypt(sequenceorder)}`);
};
export const createmanualValidation = async (formData: any): Promise<any> => {
  return postAPI(API_URL.QUESTIONNAIRE_MANUAL_VALIDATION, formData);
};
export const updateAuditPlanning = async (
  id: string,
  formData: any
): Promise<any> => {
  return postAPI(`${API_URL.UPDATE_AUDIT_PLANNING}/${id}`, formData);
};

export const getAuditingList = async (offset: number): Promise<any> => {
  return getAPI(`${API_URL.AUDITING_LIST}?o=${encrypt(offset)}`);
};
export const deleteAuditDetails = async (id: number): Promise<any> => {
  return deleteAPI(`${API_URL.DELETING_LIST}/${id}`);
};
export const moveAuditClosure = async (
  offset: string,
  data?: any
): Promise<any> => {
  return postAPI(`${API_URL.AUDITING_MOVE_CLOSURE}?a=${encrypt(offset)}`, data);
};
export const closeAuditing = async (
  offset: string,
  data?: any
): Promise<any> => {
  return postAPI(`${API_URL.AUDITING_CLOSE}?a=${encrypt(offset)}`, data);
};
export const reviewAuditing = async (
  offset: string,
  data?: any
): Promise<any> => {
  return postAPI(`${API_URL.AUDITING_REVIEW}?a=${encrypt(offset)}`, data);
};
export const getKickoffList = async (offset: number): Promise<any> => {
  return getAPI(`${API_URL.KICKOFF_LIST}?o=${encrypt(offset)}`);
};

export const getAuditingReport = async (): Promise<any> => {
  return getAPI(`${API_URL.AUDIT_REPORT}`);
};
export const getSequenceList = async (): Promise<any> => {
  return getAPI(`${API_URL.AUDIT_SEQUENCE_LIST}`);
};

export const getAICall = async (): Promise<any> => {
  return getAPI("/api/v1/auditing/ai/validate");
};
// LDAP Configuration
export const getLDAPDetails = async (): Promise<any> => {
  return getAPI(`${API_URL.LDAP_DETAILS}`);
}

export const updateDirectory = async (username: string, formData: any): Promise<any> => {
  return postAPI(`${API_URL.DIRECTORY_UPDATE}`, formData);
}

export const testLDAPConnection = async (formData: any): Promise<any> => {
  return postAPI(`${API_URL.TEST_CONNECTION}`, formData); 
};

//AI Interaction
export const getAIInteractionList = async (): Promise<any> => {
  return getAPI(`${API_URL.AI_INTERACTION}`);
}
export const saveAIInteraction = async (formData: any): Promise<any> => {
  return putAPI(`${API_URL.SAVE_AI_INTERACTION}`, formData);
}
export const getLLMConfigurationList = async (type: string): Promise<any> => {
  return getAPI(`${API_URL.LLM_CONFIGURATION_LIST}?type=QUERY`);
}

export const getAISuggestions = async (
  auditee: any,
  auditMode: number,
  auditLevel: number
): Promise<any> => {
  return getAPI(
    `${API_URL.AI_SUGGESTIONS}?a=${encrypt(auditee)}&t=${encrypt(auditMode)}&l=${encrypt(auditLevel)}`
  );
};

//organization mapper
export const getorganization = async (): Promise<any> => {
  return getAPI(`${API_URL.AUDIT_ORGINIZATION}`);
};
export const getThemes = async (): Promise<any> => {
  return getAPI(`${API_URL.AUDIT_ORGTHEME}`);
};
export const getorgApplication = async (id: number): Promise<any> => {
  return getAPI(`${API_URL.AUDIT_ORGAPPLICATION}/${id}`);
};
export const getorguser = async (id: number): Promise<any> => {
  return getAPI(`${API_URL.AUDIT_ORGMEMBER}/${id}`);
};

export const getReveiwList = async (offset: number): Promise<any> => {
  return getAPI(`${API_URL.REVIEW_LIST}?o=${encrypt(offset)}`);
};
export const getinReveiwList = async (offset: number): Promise<any> => {
  return getAPI(`${API_URL.IN_REVIEW_LIST}?o=${encrypt(offset)}`);
};
export const getAIcomplaince = async (
  ContextName: string,
  formData: any
): Promise<any> => {
  return postAPI(API_URL.AI_COMPLAINCE + "/" + ContextName, formData);
};
export const getCompleted = async (offset: number): Promise<any> => {
  return getAPI(`${API_URL.COMEPLETED_LIST}?o=${encrypt(offset)}`);
};
export const getClosure = async (offset: number): Promise<any> => {
  return getAPI(`${API_URL.AUDIT_CLOSURE_LIST}?a=${encrypt(offset)}`);
};
export const getOverview = async (): Promise<any> => {
  return getAPI(`${API_URL.OVERVIEW_LIST}`);
};
export const getAuditReivewDetails = async (auditId: number): Promise<any> => {
  return getAPI(`${API_URL.AUDIT_REVIEW_DETAILS}?a=${encrypt(auditId)}`);
};

export const getQuestionResponse = async (
  auditId: number,
  orgId: number
): Promise<any> => {
  return getAPI(
    `${API_URL.QUESTIONS_RESPONSE}?a=${encrypt(auditId)}&t=${encrypt(orgId)}`
  );
};

export const getAttendeesList = async (auditId: number): Promise<any> => {
  return getAPI(`${API_URL.ATTENDEES_LIST}?a=${encrypt(auditId)}`);
};

export const getQuestionsList = async (auditId: any): Promise<any> => {
  return getAPI(`${API_URL.QUESTIONS_LIST}?a=${auditId}`);
};

export const sendAuditQuestions = async (formData: any): Promise<any> => {
  return postAPI(API_URL.SEND_AUDTI_QUESTIONS, formData);
};

export const getJVendorsList = async (): Promise<VendorListResponse[]> => {
  return await getAPI(API_URL.VENDOR_LIST);
};

export const getJUsersList = async (): Promise<any[]> => {
  return await getAPI(API_URL.USERS_LIST);
};
export const getUsersByRole = async (roleType: string): Promise<any[]> => {
  return await getAPI(`${API_URL.USERS_LIST_BY_ROLE}/${roleType}`);
};
export const getUserGroups = async (): Promise<any[]> => {
  return await getAPI(API_URL.USER_GROUPS);
};
export const getUserGroupsList = async (): Promise<any[]> => {
  return await getAPI(API_URL.USER_GROUPS_LIST);
};
export const getRoleMappingRoles = async (): Promise<any[]> => {
  return await getAPI(API_URL.ROLE_MAPPING_ROLES);
};
export const registerUserGroups = async (
  groupNames: string[],
  roleType: string
): Promise<any> => {
  return postAPI(API_URL.USER_GROUPS_REGISTER, { groupNames, roleType });
};
export const submitVendor = async (formData: any): Promise<any> => {
  return postAPI(API_URL.VENDOR_CREATE, formData);
};
export const registerUser = async (formData: any): Promise<any> => {
  return postAPI(API_URL.USERS_REGISTER, formData);
};
export const getVendorById = async (id: string): Promise<any> => {
  return getAPI(API_URL.VENDOR_VIEW + "/" + id);
};
export const getOnboardingById = async (id: string): Promise<any> => {
  return getAPI(API_URL.ONBOARDING_VIEW_RITM + "/" + id);
};

export const getReportReviewData = async (id: string): Promise<any> => {
  return getAPI(API_URL.REPORT_REVIEW_DATA + "?auditingId=" + id);
};

export const getBedrockScore = async (): Promise<any> => {
  return getAPI("/api/v1/jd/score");
};
export const getQuestionnaireDetails = async (id: string): Promise<any> => {
  return getAPI(API_URL.QUESTIONNAIRE_DETAILS + "?a=" + encrypt(id));
};

export const getRecommendations = async (
  id: string,
  offset: number
): Promise<any> => {
  return getAPI(`${API_URL.RECOMMENDATION_LIST}/${id}?o=${encrypt(offset)}`);
};

export const getAcknowledgments = async (
  id: string,
  offset: number
): Promise<any> => {
  return getAPI(`${API_URL.ACKNOWLEDGMENT_LIST}/${id}?o=${encrypt(offset)}`);
};

export const getInterviewSchedules = async (
  type: string,
  offset: number
): Promise<any> => {
  return getAPI(
    `${API_URL.INTERVIEW_SCHEDULE_LIST}?type=${type}&o=${encrypt(offset)}`
  );
};

export const getShortlistedCandidates = async (
  offset: number
): Promise<any> => {
  return getAPI(`${API_URL.SHORTLISTED_LIST}?o=${encrypt(offset)}`);
};

export const getOnboardingList = async (offset: number): Promise<any> => {
  return getAPI(`${API_URL.ONBOARDING_LIST}?o=${encrypt(offset)}`);
};

export const getOnboardingExpiringList = async (
  offset: number
): Promise<any> => {
  return getAPI(`${API_URL.ONBOARDING_EXPIRING_LIST}?o=${encrypt(offset)}`);
};

export const downloadDocument = async (id: string): Promise<any> => {
  return downloadFile(API_URL.DOCUMENT_DOWNLOAD + "/" + id);
};

export const getResume = (id: string) => {
  return getAPI(API_URL.CANDIDATE_RESUME + "/" + encrypt(id));
};

export const getRoleRequestStats = () => {
  return getAPI(API_URL.ROLE_REQUEST_STATS);
};

export const getInterviewScheduleStats = () => {
  return getAPI(API_URL.INTERVIEW_SCHEDULE_STATS);
};

export const getFeedbackStats = () => {
  return getAPI(API_URL.FEEDBACK_STATS);
};

export const fetchUserInfo = () => {
  return getAPI(API_URL.USER_INFO);
};

export const getRoleRequestById = async (id: string): Promise<any> => {
  return getAPI(API_URL.ROLE_REQUEST_VIEW + "/" + id);
};

export const downloadResume = async (id: string): Promise<any> => {
  return downloadFile(API_URL.CANDIDATE_DOWNLOAD_RESUME + "/" + encrypt(id));
};

export const updateSquad = async (
  request: UpdateSquadRequest
): Promise<any> => {
  return postAPI(API_URL.UPDATE_SQUAD, request);
};

export const getAIResponse = async (request: any): Promise<any> => {
  return postAPI(API_URL.AI_CHAT_BOT_API, request);
};

export const getRecommendationsForSchedule = async (
  ids: string
): Promise<any> => {
  return getAPI(API_URL.RECOMMENDATION_LIST_FOR_SCHEDULE + "/" + ids);
};

export const submitInterviewSchedule = async (formData: any): Promise<any> => {
  return postAPI(API_URL.INTERVIEW_SCHEDULE_CREATE, formData);
};

export const submitMidCareer = async (formData: any): Promise<any> => {
  return postMultipartAPI(API_URL.MID_CAREER_RESUMES_SCORE, formData);
};

export const saveMidCareerCandidate = async (formData: any): Promise<any> => {
  return postAPI(API_URL.MID_CAREER_REQUEST_SAVE, formData);
};

export const saveRenewRequest = async (formData: any): Promise<any> => {
  return postAPI(API_URL.RENEW_REQUEST_SAVE, formData);
};

export const auditInformation = async (): Promise<any> => {
  return getAPI(API_URL.AUDIT_INFORMATION);
};

export const getAuditTimeLine = async (auditingId: string): Promise<any> => {
  return getAPI(API_URL.AUDIT_TIMELINE_DETAILS + "=" + auditingId);
};

export const getFindsandissuesChartData = async (
  auditingId: number
): Promise<any> => {
  return getAPI(API_URL.FINDS_ISSUE_CHARTDATA + "/" + auditingId);
};

export const getFindsandissuestableData = async (
  auditingId: number,
  severity: string
): Promise<any> => {
  return getAPI(
    API_URL.FINDS_ISSUE_TABLEDATA +
    "?auditingId=" +
    auditingId +
    "&severity=" +
    severity
  );
};
export const getEscalation = async (auditingId: number): Promise<any> => {
  return getAPI(API_URL.AUDIT_ESCALATION + "/" + auditingId);
};
export const getProgressmetrics = async (auditingId: number): Promise<any> => {
  return getAPI(API_URL.AUDIT_PROGRESSMETRICS + "/" + auditingId);
};

export const getGeneralInfo = async (auditingId: string): Promise<any> => {
  return getAPI(API_URL.AUDIT_GENERALINFO + "=" + auditingId);
};

export const getResourceUtilization = async (
  auditingId: number
): Promise<any> => {
  return getAPI(API_URL.AUDIT_RESOURCEUTLIZATION + "/" + auditingId);
};

export const sendReminder = async (payload: any): Promise<any> => {
  return postAPI(API_URL.SEND_REMINDER, payload);
};

export const getApplicationList = async (auditID: number): Promise<any> => {
  return getAPI(`${API_URL.GET_APPLICATIONS_LIST}?a=${auditID}`);
};
export const qaSuggestion = async (payload: any): Promise<any> => {
  return postAPI(API_URL.QA_SUGGESTION, payload);
};

export const getResponseAnalysis = async (auditID: number): Promise<any> => {
  return getAPI(`${API_URL.RESPONSE_ANALYSIS}?a=${encrypt(auditID)}`);
};
export const getResponseAnalysisByTheme = async (
  auditID: number
): Promise<any> => {
  return getAPI(`${API_URL.RESPONSE_ANALYSIS_BY_THEME}?a=${encrypt(auditID)}`);
};
export const getResponseAnalysisByTime = async (
  auditID: number,
  offset: number
): Promise<any> => {
  return getAPI(
    `${API_URL.RESPONSE_ANALYSIS_BY_TIME}?a=${encrypt(auditID)}&o=${encrypt(offset)}`
  );
};

export const getAuditeeQuestionAnalysis = async (
  auditID: number,
  questionId: number,
  offset: number
): Promise<any> => {
  return getAPI(
    `${API_URL.RESPONSE_ANALYSIS_BY_AUDITEE_TIME}?a=${encrypt(auditID)}&q=${encrypt(questionId)}&o=${encrypt(offset)}`
  );
};

export const policyComparison = async (formData: any): Promise<any> => {
  return postAPI(API_URL.POLICY_COMPARISON_RESPONSE, formData);
};
export const generatePurposeArtifacts = async (question: string): Promise<any> => {
  return postAPI(API_URL.GENERATE_PURPOSE_ARTIFACTS, { question });
};

export const getPolicyClauseList = async (policyName: any): Promise<any> => {
  return getAPI(`${API_URL.POLICY_CLAUSE_LIST}?policyName=${policyName}`);
};

export const getLLMConfigList = async (type: string): Promise<any[]> => {
  return getAPI(`${API_URL.LLM_CONFIG_LIST}?type=${type}`);
};

export const saveLLMConfig = async (data: any): Promise<any> => {
  return postAPI(API_URL.LLM_CONFIG_SAVE, data);
};

export const updateLLMConfig = async (id: number, data: any): Promise<any> => {
  return putAPI(`${API_URL.LLM_CONFIG_UPDATE}/${id}`, data);
};

export const deleteLLMConfig = async (id: number): Promise<any> => {
  return deleteAPI(`${API_URL.LLM_CONFIG_DELETE}/${id}`);
};

export const getAuditOverviewGraph = async (
  networkGraphRootType: string,
  filterValue: string,
  fromDate: string,
  toDate: string
): Promise<any> => {
  const payload = {
    networkGraphRootType,
    filterValue,
    fromDate,
    toDate
  };

  return postAPI(API_URL.AUDIT_OVERVIEW_GRAPH, payload);
};

/**
 *
 * @param apiUrl
 * @returns
 */
const getAPI = async (apiUrl: string) => {
  const config = {
    withCredentials: true,
  };

  try {
    const response = await axios.get(apiUrl, config);
    return response.data; // Return the data if successful
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    if (error.response?.status === 401) {
      sessionStorage.setItem("sessionExpired", "true");
      return;
    }
    throw error;
  }
};
export { getAPI };

const downloadFile = async (apiUrl: string) => {
  const config = {
    withCredentials: true,
  };

  try {
    return await axios.get(apiUrl, { responseType: "blob", ...config });
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    if (error.response?.status === 401) {
      sessionStorage.setItem("sessionExpired", "true");
      return;
    }
    throw error;
  }
};
export { downloadFile };
/**
 *
 * @param apiUrl
 * @param data
 * @returns
 */

const postAPI = async (apiUrl: string, data: any) => {
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
  };

  try {
    const response = await axios.post(apiUrl, data, config);
    return response.data; // Return the data if successful
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    if (error.response?.status === 401) {
      sessionStorage.setItem("sessionExpired", "true");
      return;
    }
    throw error;
  }
};

export { postAPI };

const postMultipartAPI = async (apiUrl: string, data: any) => {
  const config = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    withCredentials: true,
  };

  try {
    const response = await axios.post(apiUrl, data, config);
    return response.data; // Return the data if successful
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    if (error.response?.status === 401) {
      sessionStorage.setItem("sessionExpired", "true");
      return;
    }
    throw error;
  }
};

export { postMultipartAPI };

/**
 *
 * @param apiUrl
 * @param data
 * @returns
 */

const deleteAPI = async (apiUrl: string) => {
  const config = {
    withCredentials: true,
  };

  try {
    const response = await axios.delete(apiUrl, config);
    return response.data; // Return the data if successful
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    if (error.response?.status === 401) {
      sessionStorage.setItem("sessionExpired", "true");
      return;
    }
    throw error;
  }
};
export { deleteAPI };

const putAPI = async (apiUrl: string, data: any) => {
  const config = {
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
  };

  try {
    const response = await axios.put(apiUrl, data, config);
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    if (error.response?.status === 401) {
      sessionStorage.setItem("sessionExpired", "true");
      return;
    }
    throw error;
  }
};
export { putAPI };

// Team APIs
export const getTeamList = async (): Promise<any[]> => {
  return await getAPI(API_URL.TEAM_LIST);
};
export const createTeam = async (formData: any): Promise<any> => {
  return postAPI(API_URL.TEAM_CREATE, formData);
};
export const updateTeam = async (teamId: number, formData: any): Promise<any> => {
  return putAPI(`${API_URL.TEAM_UPDATE}/${teamId}`, formData);
};
export const deleteTeamById = async (teamId: number): Promise<any> => {
  return deleteAPI(`${API_URL.TEAM_DELETE}/${teamId}`);
};

export const updateUserStatus = async (userId: number, isActive: boolean): Promise<any> => {
  return putAPI(API_URL.UPDATE_USER_STATUS, { userId, isActive });
};
